/* ================================
  Cart Drawer Component
================================ */
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (e) => {
      if (e.code === 'Escape') {
        this.close();
      }
    });

    this.querySelector('#CartDrawer-Overlay')?.addEventListener(
      'click',
      this.close.bind(this)
    );

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (e) => {
      e.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (e) => {
      if (e.code.toUpperCase() === 'SPACE' || e.code === 'Enter') {
        e.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.activeElement = triggeredBy;
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        if (
          typeof trapFocus === 'function' &&
          containerToTrapFocusOn &&
          focusElement
        ) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    setTimeout(() => {
      window.reloadCartDrawerRecommendations?.();
    }, 200);

    setTimeout(() => {
      window.updateFreeShippingBar?.();
    }, 250);

    setTimeout(() => {
      window.loadRecentlyViewedCart?.();
    }, 200);
  }

  close() {
    this.classList.remove('active');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove('overflow-hidden');
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const element = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!element) return;

      const sectionHTML = parsedState.sections?.[section.id];

      if (!sectionHTML) return;

      element.innerHTML = this.getSectionInnerHTML(
        sectionHTML,
        section.selector
      );
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener(
        'click',
        this.close.bind(this)
      );

      window._cartDrawerAfterRender?.();

      this.open();
    }, 100);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const element = doc.querySelector(selector);

    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);


/* ================================
  Cart Drawer Items
================================ */
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  updateQuantity(line, quantity, name) {
    super.updateQuantity(line, quantity, name);

    // super.updateQuantity() already refreshes the drawer + cart-icon-bubble
    // sections — no need to call refreshCartDrawer() again here, that was
    // causing a double fetch and a 1s delay before recommendations showed.
    setTimeout(() => {
      window.reloadCartDrawerRecommendations?.();
    }, 300);
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


/* ================================
  Cart Drawer After Render
================================ */
window._cartDrawerAfterRender = function () {
  document
    .querySelector('#CartDrawer-Overlay')
    ?.addEventListener('click', () => {
      document.querySelector('cart-drawer')?.close();
    });

  if (typeof initShippingToggle === 'function') {
    initShippingToggle();
  }

  if (typeof syncToggleWithCart === 'function') {
    syncToggleWithCart();
  }

  setTimeout(() => {
    window.updateFreeShippingBar?.();
  }, 100);

  setTimeout(() => {
    window.reloadCartDrawerRecommendations?.();
  }, 300);

  setTimeout(() => {
    window.loadRecentlyViewedCart?.();
  }, 300);
};


/* ================================
  Refresh Cart Drawer
================================ */
window.refreshCartDrawer = async function () {
  try {
    const response = await fetch(
      '/?sections=cart-drawer,cart-icon-bubble',
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to refresh cart drawer');
    }

    const sections = await response.json();

    /* Cart Drawer */
    const drawer = document.querySelector('#CartDrawer');

    if (drawer && sections['cart-drawer']) {
      const doc = new DOMParser().parseFromString(
        sections['cart-drawer'],
        'text/html'
      );

      const newDrawer = doc.querySelector('#CartDrawer');

      if (newDrawer) {
        drawer.innerHTML = newDrawer.innerHTML;
      }
    }

    /* Cart Bubble */
    const bubble = document.querySelector('#cart-icon-bubble');

    if (bubble && sections['cart-icon-bubble']) {
      const doc = new DOMParser().parseFromString(
        sections['cart-icon-bubble'],
        'text/html'
      );

      const newBubble = doc.querySelector('#cart-icon-bubble');

      if (newBubble) {
        bubble.innerHTML = newBubble.innerHTML;
      }
    }

    /* Reinitialize after DOM replacement */
    setTimeout(() => {
      if (typeof initShippingToggle === 'function') {
        initShippingToggle();
      }

      if (typeof syncToggleWithCart === 'function') {
        syncToggleWithCart();
      }

      if (typeof initRecommendationSlider === 'function') {
        initRecommendationSlider();
      }

      window.reloadCartDrawerRecommendations?.();
      window.loadRecentlyViewedCart?.();
    }, 150);

  } catch (error) {
    console.error('Cart drawer refresh failed:', error);
  }
};


/* ================================
  Recommendation Products
  (only ever shown when cart is NOT empty AND the Bag tab — not the
  Wishlist tab — is active. See cart-drawer.liquid / wishlist.js.)
================================ */
const currencySymbol = '₹';

function formatMoney(cents) {
  if (cents == null) return '';
  let amount = (cents / 100).toFixed(2);
  amount = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return currencySymbol + amount;
}

function swatchColor(name) {
  const map = {
    gold: '#D4AF37',
    'yellow gold': '#D4AF37',
    silver: '#C0C0C0',
    'rose gold': '#B76E79',
    'white gold': '#E8E8E8',
    black: '#111111',
    white: '#FFFFFF'
  };
  return map[(name || '').toLowerCase()] || '#cccccc';
}

/*
 * Returns true only when the Wishlist tab button is the active one.
 * Falls back to false (i.e. "safe to show recommendations") if the
 * tab markup isn't on the page at all, so this never breaks contexts
 * that don't have the wishlist tabs.
 */
function isWishlistTabActive() {
  const wishlistTabBtn = document.querySelector('.cd-tab-btn[data-tab="wishlist"]');
  return !!(wishlistTabBtn && wishlistTabBtn.classList.contains('active'));
}

window.loadCartDrawerRecommendations = function (productId) {
  const container = document.getElementById('cart-recommendations');
  if (!container || !productId) return;

  // Recommendations belong to the Bag tab only. If the Wishlist tab is
  // currently open, don't even fetch — just make sure the panel stays
  // hidden and bail out.
  if (isWishlistTabActive()) {
    container.classList.remove('active');
    container.style.display = 'none';
    return;
  }

  const track = container.querySelector('.recommendations-wrapper');
  if (!track) return;

  fetch(`/recommendations/products.json?product_id=${productId}&limit=6`)
    .then((res) => {
      if (!res.ok) throw new Error('Recommendation request failed');
      return res.json();
    })
    .then((data) => {
      // The fetch above is async — re-check the active tab now, in
      // case the user switched to Wishlist while this was in flight.
      if (isWishlistTabActive()) {
        container.classList.remove('active');
        container.style.display = 'none';
        return;
      }

      const products = (data.products || []).filter(
        (product) => product.handle !== 'shipping-protection' && product.variants?.length
      );

      if (!products.length) {
        container.classList.remove('active');
        return;
      }

      track.innerHTML = '';

      products.forEach((product) => {
        const image = product.featured_image || '';
        const title = product.title || '';

        // Unique swatches based on variant option1 (adjust index if color isn't option1)
        const seen = new Set();
        const swatches = product.variants.filter((v) => {
          if (!v.option1 || seen.has(v.option1)) return false;
          seen.add(v.option1);
          return true;
        });

        const defaultVariant = product.variants.find((v) => v.available) || product.variants[0];
        if (!defaultVariant) return;

        const card = document.createElement('div');
        card.className = 'cd-rec-card';

        card.innerHTML = `
          <div class="cd-rec-card__img-wrap">
            ${image ? `<img src="${image}" alt="${title.replace(/"/g, '&quot;')}" loading="lazy">` : ''}
          </div>
          <span class="cd-rec-card__title">${title}</span>
          <span class="cd-rec-card__price">${formatMoney(defaultVariant.price)}</span>
          ${
            swatches.length > 1
              ? `<div class="cd-rec-card__swatches">
                  ${swatches
                    .map(
                      (v) => `
                    <button
                      type="button"
                      class="cd-rec-card__swatch${v.id === defaultVariant.id ? ' is-selected' : ''}"
                      style="background-color:${swatchColor(v.option1)}"
                      data-variant-id="${v.id}"
                      data-price="${v.price}"
                      title="${v.option1}"
                    ></button>`
                    )
                    .join('')}
                </div>`
              : ''
          }
          <button
            type="button"
            class="cd-rec-card__add-btn recommandation_add_product"
            data-variant-id="${defaultVariant.id}"
          >
            ADD TO BAG
          </button>
        `;

        track.appendChild(card);
      });

      container.classList.add('active');
      container.style.display = '';
    })
    .catch((error) => {
      console.error('Recommendation fetch error:', error);
      container.classList.remove('active');
    });
};

/* Swatch selection (recommendation cards) */
document.addEventListener('click', function (e) {
  const swatch = e.target.closest('.cd-rec-card__swatch');
  if (!swatch) return;

  const card = swatch.closest('.cd-rec-card');
  if (!card) return;

  card.querySelectorAll('.cd-rec-card__swatch').forEach((el) => el.classList.remove('is-selected'));
  swatch.classList.add('is-selected');

  const addBtn = card.querySelector('.recommandation_add_product');
  if (addBtn) addBtn.dataset.variantId = swatch.dataset.variantId;
});

/* Swatch selection (recently viewed cards) */
document.addEventListener('click', function (e) {
  const swatch = e.target.closest('.rv-cart-card__swatch');
  if (!swatch) return;

  const card = swatch.closest('.rv-cart-card');
  if (!card) return;

  card.querySelectorAll('.rv-cart-card__swatch').forEach((el) => el.classList.remove('is-selected'));
  swatch.classList.add('is-selected');

  const addBtn = card.querySelector('.recommandation_add_product');
  if (addBtn) addBtn.dataset.variantId = swatch.dataset.variantId;
});

/* ================================
  Reload Recommendations
  Only meaningful when the cart has items AND the Bag tab is active —
  an empty cart never shows the "Why not add..." panel, and neither
  does the Wishlist tab.
================================ */
window.reloadCartDrawerRecommendations = function () {
  if (isWishlistTabActive()) {
    const container = document.getElementById('cart-recommendations');
    if (container) {
      container.classList.remove('active');
      container.style.display = 'none';
    }
    return;
  }

  fetch('/cart.js')
    .then((res) => res.json())
    .then((cart) => {
      if (!cart.items.length) return;

      // Shipping protection ko skip karo, real product dhundo
      const realItems = cart.items.filter(
        (item) =>
          Number(item.variant_id) !== Number(window.shippingVariantId) &&
          item.handle !== 'shipping-protection'
      );

      if (!realItems.length) return;

      const lastProductId = realItems[realItems.length - 1].product_id;
      window.loadCartDrawerRecommendations?.(lastProductId);
    })
    .catch((error) => {
      console.error('Cart recommendation reload error:', error);
    });
};

/* ================================
  Recommendation / Recently-Viewed Add To Cart
  (single handler — shared by .cd-rec-card and .rv-cart-card
  quick-add buttons via the common class)
================================ */
document.addEventListener('click', async function (e) {
  const button = e.target.closest('.recommandation_add_product');
  if (!button) return;

  e.preventDefault();

  const variantId = button.dataset.variantId;
  if (!variantId) return;

  const originalText = button.innerHTML;

  button.disabled = true;
  button.innerHTML = '...';

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: Number(variantId),
        quantity: 1
      })
    });

    if (!response.ok) {
      throw new Error('Failed to add product');
    }

    await response.json();

    button.innerHTML = '✓';
    button.classList.add('is-added');

    await window.refreshCartDrawer();

    document.querySelector('cart-drawer')?.open();

    setTimeout(() => {
      button.disabled = false;
      button.innerHTML = originalText;
      button.classList.remove('is-added');
    }, 1500);

  } catch (error) {
    console.error('Add to cart error:', error);

    button.disabled = false;
    button.innerHTML = originalText;
  }
});


/* ================================
  Shipping Protection
================================ */
window.shippingVariantId = 48950224912624;

function initShippingToggle() {
  const toggle = document.getElementById(
    'shippingProtectionToggle'
  );

  if (!toggle) return;

  toggle.removeEventListener(
    'change',
    handleShippingToggle
  );

  toggle.addEventListener(
    'change',
    handleShippingToggle
  );
}

function handleShippingToggle(e) {
  if (e.target.checked) {
    addShippingProtection();
  } else {
    removeShippingProtection();
  }
}

function syncToggleWithCart() {
  const toggle = document.getElementById(
    'shippingProtectionToggle'
  );

  if (!toggle) return;

  fetch('/cart.js')
    .then((res) => res.json())
    .then((cart) => {
      const item = cart.items.find(
        (item) =>
          Number(item.variant_id) ===
          Number(window.shippingVariantId)
      );

      toggle.checked = !!item;
    });
}

function addShippingProtection() {
  fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify({
      id: window.shippingVariantId,
      quantity: 1
    })
  })
    .then((res) => {
      if (!res.ok) {
        throw new Error('Failed to add shipping protection');
      }

      return res.json();
    })
    .then(() => {
      window.refreshCartDrawer?.();
    })
    .catch((error) => {
      console.error('Shipping protection error:', error);
    });
}

function removeShippingProtection() {
  fetch('/cart.js')
    .then((res) => res.json())
    .then((cart) => {
      const item = cart.items.find(
        (item) =>
          Number(item.variant_id) ===
          Number(window.shippingVariantId)
      );

      if (!item) return;

      return fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: item.key,
          quantity: 0
        })
      });
    })
    .then((res) => {
      if (res && !res.ok) {
        throw new Error(
          'Failed to remove shipping protection'
        );
      }

      window.refreshCartDrawer?.();
    })
    .catch((error) => {
      console.error(
        'Remove shipping protection error:',
        error
      );
    });
}


/* ================================
  Recommendation Slider
================================ */
function initRecommendationSlider() {
  const container =
    document.getElementById('cart-recommendations');

  if (!container) return;

  const wrapper =
    container.querySelector(
      '.recommendations-wrapper'
    );

  if (!wrapper) return;

  container
    .querySelectorAll('.rec-slider-btn')
    .forEach((el) => el.remove());

  const prevBtn =
    document.createElement('button');

  prevBtn.className =
    'rec-slider-btn rec-slider-btn--prev';

  prevBtn.type = 'button';

  prevBtn.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2.5"
      stroke-linecap="round"
      stroke-linejoin="round">
      <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
  `;

  const nextBtn =
    document.createElement('button');

  nextBtn.className =
    'rec-slider-btn rec-slider-btn--next';

  nextBtn.type = 'button';

  nextBtn.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2.5"
      stroke-linecap="round"
      stroke-linejoin="round">
      <polyline points="9 18 15 12 9 6"></polyline>
    </svg>
  `;

  const header =
    container.querySelector('.rec-slider-header');

  if (header) {
    header.appendChild(prevBtn);
    header.appendChild(nextBtn);
  }

  const scrollAmount = 280;

  prevBtn.addEventListener('click', () => {
    wrapper.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth'
    });
  });

  nextBtn.addEventListener('click', () => {
    wrapper.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  });

  function updateArrows() {
    const atStart = wrapper.scrollLeft <= 5;

    const atEnd =
      wrapper.scrollLeft +
        wrapper.clientWidth >=
      wrapper.scrollWidth - 5;

    prevBtn.style.opacity = atStart ? '0.3' : '1';
    nextBtn.style.opacity = atEnd ? '0.3' : '1';

    prevBtn.style.pointerEvents =
      atStart ? 'none' : 'auto';

    nextBtn.style.pointerEvents =
      atEnd ? 'none' : 'auto';
  }

  wrapper.addEventListener(
    'scroll',
    updateArrows,
    { passive: true }
  );

  updateArrows();

  /* Drag Scroll */
  let isDown = false;
  let startX = 0;
  let scrollLeft = 0;

  wrapper.addEventListener('mousedown', (e) => {
    isDown = true;

    startX =
      e.pageX -
      wrapper.offsetLeft;

    scrollLeft =
      wrapper.scrollLeft;

    wrapper.style.cursor = 'grabbing';
  });

  wrapper.addEventListener('mouseleave', () => {
    isDown = false;
    wrapper.style.cursor = 'grab';
  });

  wrapper.addEventListener('mouseup', () => {
    isDown = false;
    wrapper.style.cursor = 'grab';
  });

  wrapper.addEventListener('mousemove', (e) => {
    if (!isDown) return;

    e.preventDefault();

    const x =
      e.pageX -
      wrapper.offsetLeft;

    const walk =
      (x - startX) * 1.5;

    wrapper.scrollLeft =
      scrollLeft - walk;
  });
}


/* ================================
  Recently Viewed (Cart Drawer — Empty Cart state)
================================ */

/* Map product tags to a display badge. First match wins. */
function getRecentlyViewedBadge(tags) {
  if (!Array.isArray(tags)) return null;

  const lower = tags.map((t) => t.toLowerCase());

  if (lower.some((t) => t.includes('trending'))) return 'Trending';
  if (lower.some((t) => t.includes('add-on') || t.includes('addon'))) return 'Add-On';
  if (lower.some((t) => t.includes('new'))) return 'New in Stock';

  return null;
}

window.loadRecentlyViewedCart = function () {
  const container = document.getElementById('RecentlyViewed-CartDrawer');
  if (!container) return;

  const track = container.querySelector('.ec-recently-viewed__track');
  if (!track) return;

  let handles = [];
  try {
    handles = JSON.parse(localStorage.getItem('recently_viewed_products') || '[]');
  } catch (e) {
    handles = [];
  }

  if (!handles.length) {
    container.style.display = 'none';
    return;
  }

  Promise.all(
    handles.slice(0, 8).map((handle) =>
      fetch(`/products/${handle}.js`)
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null)
    )
  ).then((products) => {
    products = products.filter(Boolean);

    if (!products.length) {
      container.style.display = 'none';
      return;
    }

    track.innerHTML = '';

    products.forEach((product) => {
      const variant = product.variants.find((v) => v.available) || product.variants[0];
      if (!variant) return;

      const image = product.featured_image || (product.images && product.images[0]) || '';
      const badge = getRecentlyViewedBadge(product.tags);

      const seen = new Set();
      const swatches = product.variants.filter((v) => {
        if (!v.option1 || seen.has(v.option1)) return false;
        seen.add(v.option1);
        return true;
      });

      const card = document.createElement('div');
      card.className = 'rv-cart-card';
      card.innerHTML = `
        <a href="${product.url}" class="rv-cart-card__img-link">
          ${image ? `<img src="${image}" alt="${product.title.replace(/"/g, '&quot;')}" loading="lazy">` : ''}
          ${badge ? `<span class="rv-cart-card__badge">${badge}</span>` : ''}
        </a>
        <a href="${product.url}" class="rv-cart-card__title">${product.title}</a>
        <span class="rv-cart-card__price">${formatMoney(variant.price)}</span>
        ${
          swatches.length > 1
            ? `<div class="rv-cart-card__swatches">
                ${swatches
                  .map(
                    (v) => `
                  <button
                    type="button"
                    class="rv-cart-card__swatch${v.id === variant.id ? ' is-selected' : ''}"
                    style="background-color:${swatchColor(v.option1)}"
                    data-variant-id="${v.id}"
                    title="${v.option1}"
                  ></button>`
                  )
                  .join('')}
              </div>`
            : ''
        }
      `;

      track.appendChild(card);
    });

    container.style.display = 'block';
  });
};


/* ================================
  DOM Ready
================================ */
document.addEventListener('DOMContentLoaded', () => {
  initShippingToggle();
  syncToggleWithCart();
  initRecommendationSlider();
  window.loadRecentlyViewedCart?.();
});
